import React, { useState, useEffect, useCallback } from "react";
import { Layout, message, Table, Tag, Button, Space, Tabs, Row, Col, DatePicker, Select } from "antd";
import { CheckCircleOutlined, EyeOutlined, SearchOutlined, ClearOutlined, FilterOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import { getAuthItem } from "../../../services/authService";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { useNavigate } from "react-router-dom";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;

const GRNApprovals = () => {
    const [approvals, setApprovals] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    
    // Filters state
    const [supplierFilter, setSupplierFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState([]);
    
    // Applied filters state
    const [appliedSupplier, setAppliedSupplier] = useState(undefined);
    const [appliedDateRange, setAppliedDateRange] = useState([]);

    const navigate = useNavigate();

    const fetchApprovals = useCallback(async (
        page = 1,
        limit = 10,
        tabKey = activeTab,
        supplierId = appliedSupplier,
        dates = appliedDateRange
    ) => {
        setLoading(true);
        try {
            const startDate = dates && dates[0] ? dates[0].format('YYYY-MM-DD') : "";
            const endDate = dates && dates[1] ? dates[1].format('YYYY-MM-DD') : "";
            
            const response = await api.post(`/inventory/grn/list_approvals.php`, {
                tab: tabKey,
                supplier_id: supplierId ? parseInt(supplierId) : 0,
                start_date: startDate,
                end_date: endDate,
                page,
                limit
            });
            if (response.data.status === "success") {
                setApprovals(response.data.dataset || []);
                setPagination({
                    current: page,
                    pageSize: limit,
                    total: response.data.total || 0,
                });
            }
            
            const resSups = await api.get("/masterdropdown/listof_activesuppliers.php");
            if (resSups.data && resSups.data.status === "success") {
                setSuppliers(resSups.data.dataset);
            }
        } catch (error) {
            message.error("Failed to fetch GRN approvals.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, appliedSupplier, appliedDateRange]);

    useEffect(() => {
        fetchApprovals(1, pagination.pageSize, activeTab, appliedSupplier, appliedDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, appliedSupplier, appliedDateRange]);

    const handleFilter = () => {
        setAppliedSupplier(supplierFilter);
        setAppliedDateRange(dateRange);
        fetchApprovals(1, pagination.pageSize, activeTab, supplierFilter, dateRange);
    };

    const handleClear = () => {
        setSupplierFilter(undefined);
        setDateRange([]);
        setAppliedSupplier(undefined);
        setAppliedDateRange([]);
        fetchApprovals(1, pagination.pageSize, activeTab, undefined, []);
    };

    const handleTableChange = (pag) => {
        fetchApprovals(pag.current, pag.pageSize, activeTab, appliedSupplier, appliedDateRange);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
    };



    const columns = [
        {
            title: "GRN Reference",
            dataIndex: "grn_ref",
            key: "grn_ref",
            render: (text, record) => {
                // Current system semantics: 'false' = enabled, missing = denied
                const canViewGRN = getAuthItem("USERPERMISSION_grn_list") === "false";
                return (
                    <span 
                        style={{ 
                            color: canViewGRN ? 'var(--fluid-primary)' : '#888', 
                            cursor: canViewGRN ? 'pointer' : 'not-allowed', 
                            fontWeight: 600,
                            textDecoration: canViewGRN ? 'underline' : 'none'
                        }} 
                        onClick={() => {
                            if (canViewGRN) {
                                navigate(`/grn/${record.grn_id}/view`);
                            } else {
                                message.warning("You do not have permission to view GRN details.");
                            }
                        }}
                    >
                        {text}
                    </span>
                );
            }
        },
        {
            title: "PR Reference",
            dataIndex: "po_ref",
            key: "po_ref",
            render: (text) => text ? <Tag color="blue">{text}</Tag> : "-"
        },
        {
            title: "Supplier",
            dataIndex: "supplier_name",
            key: "supplier_name"
        },
        {
            title: "Received Date",
            dataIndex: "grn_date",
            key: "grn_date",
            render: (val) => val ? dayjs(val).format('YYYY-MM-DD') : '-'
        },
        {
            title: "Grand Total Cost",
            dataIndex: "grn_final_amount",
            key: "grn_final_amount",
            align: 'right',
            render: (val) => <strong>LKR {parseFloat(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
        },
        {
            title: "Status",
            dataIndex: "grn_status",
            key: "grn_status",
            render: (status) => {
                const text = (status === 'Pending Approval' || status === 'Pending') ? 'Pending' : status;
                return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{text}</Tag>;
            }
        },
        {
            title: "Received By",
            dataIndex: "creator_name",
            key: "creator_name",
            render: (val) => val || '-'
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        className={record.grn_status === 'Pending Approval' ? "btn-review" : "btn-view"}
                        icon={record.grn_status === 'Pending Approval' ? <CheckCircleOutlined /> : <EyeOutlined />}
                        size="small"
                        onClick={() => navigate(`/grn/approve/${record.grn_id}`)}
                    >
                        {record.grn_status === 'Pending Approval' ? 'Review' : 'View'}
                    </Button>
                </Space>
            )
        }
    ];

    const tabItems = [
        { key: 'pending', label: 'Pending Approval' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />
            
            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <AppBreadcrumb items={[
                            { label: 'Inventory', link: '/inventory-items' },
                            { label: 'GRN Approvals' },
                        ]} />
                        <h1 className="fluid-page-title">
                            <CheckCircleOutlined />GRN Approvals
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: 'right' }}>
                        <p className="fluid-page-subtitle">Review and approve pending goods received notes</p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card" style={{ padding: "20px" }}>
                    <div style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Filter by Supplier:</div>
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) => {
                                      const label = option?.label || option?.children;
                                      if (Array.isArray(label)) {
                                        return label.join('').toLowerCase().includes(input.toLowerCase());
                                      }
                                      return String(label || '').toLowerCase().includes(input.toLowerCase());
                                    }}
                                    value={supplierFilter}
                                    onChange={setSupplierFilter}
                                    style={{ width: "100%" }}
                                    placeholder="Choose Supplier"
                                    allowClear
                                >
                                    {suppliers.map(sup => (
                                        <Option key={sup.value} value={Number(sup.value)}>{sup.label}</Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Filter by Date:</div>
                                <RangePicker 
                                    value={dateRange}
                                    onChange={setDateRange} 
                                    style={{ width: "100%" }} 
                                />
                            </Col>
                            <Col xs={24} md={8}>
                                <Space>
                                    <Button 
                                        type="primary" 
                                        icon={<FilterOutlined />} 
                                        onClick={handleFilter}
                                        className="fluid-btn-primary"
                                    >
                                        Filter
                                    </Button>
                                    <Button 
                                        icon={<ClearOutlined />} 
                                        onClick={handleClear}
                                        className="fluid-btn-secondary"
                                    >
                                        Clear
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </div>

                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        items={tabItems}
                        style={{ marginBottom: 16 }}
                    />

                    <Table
                        columns={columns}
                        dataSource={approvals}
                        rowKey="grn_id"
                        loading={loading}
                        bordered
                        size="middle"
                        pagination={pagination}
                        onChange={handleTableChange}
                    />
                </div>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default GRNApprovals;
